import {
  CustomFieldConfig,
  Flow,
  OnboardingApplication,
} from "../models/onboardingApplication/onboardingApplicationInterfaces";

/**
 * Whether this application presented any disclosures — the only disclosure
 * question the portal answers for itself.
 *
 * It decides whether the Disclosures tab is offered. Everything behind the tab
 * (the outcome, the agreed language, the evidence, the audit trail) is composed
 * by acquire-api's `GET /submissions/:id/disclosures` and rendered by the
 * embedded boa panel, so this deliberately stops at a boolean. An earlier draft
 * rebuilt the whole entry list here to count it, which put flow-structure
 * knowledge in two repos and gave the product two answers to the same audit
 * question.
 *
 * Costs no request: `flowData` and `onboardingApplicationData` are already in
 * the store before this page renders at all — the detail view gates its entire
 * body on both. That is what lets the panel itself load lazily, on the first
 * visit to the tab, instead of on every page load.
 *
 * Derived from the flow config crossed with the saved responses, mirroring how
 * the panel derives the real thing:
 *
 *   - Steps outside `visitedPath` are excluded: a disclosure on an abandoned
 *     branch is not part of what the applicant was ultimately asked. No tracked
 *     path means nothing to filter against, so everything counts.
 *   - A field must appear in the step's saved response. One dropped by a display
 *     condition is absent from it, and "never asked" is not "declined".
 */
export const hasDisclosures = (
  flow?: Flow,
  application?: OnboardingApplication
): boolean => {
  if (!flow || !application) {
    return false;
  }

  const { visitedPath } = application;
  const inPath = (slug: string) =>
    !visitedPath?.length || visitedPath.includes(slug);

  // `skins[0]`, matching how the applicant's review step builds its cards.
  return (flow.skins?.[0]?.steps ?? []).some((step) => {
    if (!step.slug || !inPath(step.slug)) {
      return false;
    }

    const customFields: CustomFieldConfig[] =
      (step.assets as { customFields?: CustomFieldConfig[] })?.customFields ??
      [];

    const responseFields = application.responses?.find(
      (response) => response.slug === step.slug
    )?.fields;
    if (!responseFields) {
      return false;
    }

    return customFields.some(
      (field) =>
        field.fieldType === "disclosure" &&
        !!field.fieldKey &&
        field.fieldKey in responseFields
    );
  });
};
